import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Button, IconButton, Typography, Paper, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import IosShareIcon from '@mui/icons-material/IosShare';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { getInstallPrompt, onInstallPromptChange, showInstallPrompt } from '../pwaInstall';

const STORAGE_KEY = 'pwa_banner_dismissed_at';
const REDISPLAY_DAYS = 2;
// Let the vendor actually get some work done before interrupting — asking on
// the first screen they see reads as spam and gets closed reflexively.
const SHOW_AFTER_MS = 2 * 60 * 1000;
const AUTO_HIDE_MS = 15000;

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

// Every iOS browser is WebKit underneath, so none of them fire
// `beforeinstallprompt` — on iPhone the vendor always adds the icon by hand.
// But each one keeps "Add to Home Screen" in a different place, and the
// in-app browsers inside WhatsApp / Instagram do not offer it at all.
function iosBrowser() {
  const ua = navigator.userAgent;
  if (/CriOS/.test(ua)) return 'chrome';
  if (/EdgiOS/.test(ua)) return 'edge';
  if (/FxiOS/.test(ua)) return 'firefox';
  // Real browsers carry a `Safari/` token; embedded webviews do not.
  return /Safari/.test(ua) ? 'safari' : 'webview';
}

function IosHint({ browser }) {
  const caption = { variant: 'caption', sx: { opacity: 0.85 } };

  // No way to add a home screen icon from inside a webview — the only useful
  // thing we can tell them is to get out of it first.
  if (browser === 'webview') {
    return <Typography {...caption}>Open this page in Safari to add it</Typography>;
  }

  const menuIcon =
    browser === 'safari' ? (
      <IosShareIcon sx={{ fontSize: 14 }} />
    ) : (
      <MoreHorizIcon sx={{ fontSize: 14 }} />
    );

  return (
    <>
      <Typography {...caption}>Tap</Typography>
      {menuIcon}
      <Typography {...caption}>then "Add to Home Screen"</Typography>
    </>
  );
}

function isInStandaloneMode() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function wasDismissedRecently() {
  const ts = localStorage.getItem(STORAGE_KEY);
  if (!ts) return false;
  const daysSince = (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
  return daysSince < REDISPLAY_DAYS;
}

export default function InstallPromptBanner() {
  const [show, setShow] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const iosDevice = useRef(false);
  const iosUi = useRef('safari');
  // Flips once the delay has elapsed. On Android the banner also needs the
  // install event, which may land on either side of that deadline.
  const delayPassed = useRef(false);

  useEffect(() => {
    if (isInStandaloneMode() || wasDismissedRecently()) return;

    iosDevice.current = isIOS();
    if (iosDevice.current) iosUi.current = iosBrowser();

    const maybeShow = () => {
      if (!delayPassed.current) return;
      // iOS has no install event — we show manual instructions instead.
      if (iosDevice.current || getInstallPrompt()) setShow(true);
    };

    const delayTimer = setTimeout(() => {
      delayPassed.current = true;
      maybeShow();
    }, SHOW_AFTER_MS);

    // Covers both orders: the event captured before login, and one that
    // arrives later. `appinstalled` clears it and pulls the banner back down.
    setCanInstall(Boolean(getInstallPrompt()));
    const unsubscribe = onInstallPromptChange(prompt => {
      setCanInstall(Boolean(prompt));
      if (prompt) maybeShow();
      else setShow(false);
    });

    return () => {
      clearTimeout(delayTimer);
      unsubscribe();
    };
  }, []);

  // Explicit close means "not now" — remember it. Timing out does not, since
  // the vendor most likely never looked at the screen.
  const dismiss = useCallback(() => {
    setShow(false);
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  }, []);

  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(() => setShow(false), AUTO_HIDE_MS);
    return () => clearTimeout(timer);
  }, [show]);

  const handleInstall = async () => {
    const accepted = await showInstallPrompt();
    // Either way the prompt is spent; hide the banner rather than leave a
    // dead Install button on screen.
    setShow(false);
    if (!accepted) localStorage.setItem(STORAGE_KEY, Date.now().toString());
  };

  return (
    <Slide direction="up" in={show} mountOnEnter unmountOnExit>
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          bottom: { xs: 64, sm: 16 }, // sit above bottom nav on mobile
          left: 16,
          right: 16,
          zIndex: 1400,
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderRadius: 3,
          bgcolor: '#4f46e5',
          color: 'white',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} noWrap>
            Add Vendora88 to Home Screen
          </Typography>
          {iosDevice.current ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <IosHint browser={iosUi.current} />
            </Box>
          ) : (
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Open Vendora88 in one tap
            </Typography>
          )}
        </Box>

        {!iosDevice.current && canInstall && (
          <Button
            size="small"
            variant="contained"
            onClick={handleInstall}
            startIcon={<GetAppIcon />}
            sx={{
              bgcolor: 'white',
              color: '#4f46e5',
              flexShrink: 0,
              '&:hover': { bgcolor: '#e8e8ff' },
            }}
          >
            Install
          </Button>
        )}

        <IconButton size="small" onClick={dismiss} sx={{ color: 'white', flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Slide>
  );
}
